import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Enrollment } from "@/api/enrollCourse/types";
import { fetchUserEnrollments } from "@/api/fetchUserEnrollments/fetchUserEnrollments";
import { FetchUserEnrollmentsParams } from "@/api/fetchUserEnrollments/types";
import { unenrollCourse } from "@/api/unenrollCourse/unenrollCourse";
import { UnenrollRequestParams } from "@/api/unenrollCourse/types";
import { reenrollCourse } from "@/api/reenrollCourse/reenrollCourse";
import { ReenrollRequestParams } from "@/api/reenrollCourse/types";
import { adminEnrollMember } from "@/api/adminEnrollMember/adminEnrollMember";
import { AdminEnrollMemberParams } from "@/api/adminEnrollMember/types";

/**
 * Enrollment Slice
 * Manages enrollment state for the Learning Center
 * Part of Feature 009: Course Enrollment Enhancement
 */

interface EnrollmentState {
  userEnrollments: Enrollment[];
  courseEnrollments: Enrollment[];
  selectedEnrollment: Enrollment | null;
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  limit: number;
}

const initialState: EnrollmentState = {
  userEnrollments: [],
  courseEnrollments: [],
  selectedEnrollment: null,
  loading: false,
  error: null,
  total: 0,
  page: 1,
  limit: 20,
};

// Async Thunks

/**
 * Fetch enrollments for a specific user
 */
export const fetchUserEnrollmentsThunk = createAsyncThunk(
  "enrollment/fetchUserEnrollments",
  async (params: FetchUserEnrollmentsParams, { rejectWithValue }) => {
    try {
      const response = await fetchUserEnrollments(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch user enrollments",
      );
    }
  },
);

/**
 * Cancel (unenroll) from a course
 */
export const cancelEnrollmentThunk = createAsyncThunk(
  "enrollment/cancelEnrollment",
  async (params: UnenrollRequestParams, { rejectWithValue }) => {
    try {
      const response = await unenrollCourse(params);
      return response.enrollment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to cancel enrollment",
      );
    }
  },
);

/**
 * Reactivate a cancelled enrollment
 */
export const reactivateEnrollmentThunk = createAsyncThunk(
  "enrollment/reactivateEnrollment",
  async (params: ReenrollRequestParams, { rejectWithValue }) => {
    try {
      const response = await reenrollCourse(params);
      return response.enrollment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to reactivate enrollment",
      );
    }
  },
);

/**
 * Admin: enroll a member in a course on their behalf
 */
export const adminEnrollMemberInCourse = createAsyncThunk(
  "enrollment/adminEnrollMember",
  async (params: AdminEnrollMemberParams, { rejectWithValue }) => {
    try {
      const response = await adminEnrollMember(params);
      return response.enrollment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error ?? "Failed to enroll member",
      );
    }
  },
);

// Slice

const enrollmentSlice = createSlice({
  name: "enrollment",
  initialState,
  reducers: {
    setSelectedEnrollment: (
      state,
      action: PayloadAction<Enrollment | null>,
    ) => {
      state.selectedEnrollment = action.payload;
    },
    clearEnrollmentError: (state) => {
      state.error = null;
    },
    clearUserEnrollments: (state) => {
      state.userEnrollments = [];
      state.total = 0;
      state.page = 1;
    },
  },
  extraReducers: (builder) => {
    // Fetch User Enrollments
    builder
      .addCase(fetchUserEnrollmentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserEnrollmentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Append on "Load More" (page > 1) so pagination accumulates rather than
        // replacing the list; replace on the initial/reset fetch (page 1).
        const fetchedPage = action.meta.arg.page ?? 1;
        state.userEnrollments =
          fetchedPage > 1
            ? [...state.userEnrollments, ...action.payload.enrollments]
            : action.payload.enrollments;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchUserEnrollmentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Cancel Enrollment
    builder
      .addCase(cancelEnrollmentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelEnrollmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update the enrollment in userEnrollments array
        const index = state.userEnrollments.findIndex(
          (e) => e.id === action.payload.id,
        );
        if (index !== -1) {
          state.userEnrollments[index] = action.payload;
        }
        // Update selectedEnrollment if it matches
        if (state.selectedEnrollment?.id === action.payload.id) {
          state.selectedEnrollment = action.payload;
        }
      })
      .addCase(cancelEnrollmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Reactivate Enrollment
    builder
      .addCase(reactivateEnrollmentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reactivateEnrollmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update the enrollment in userEnrollments array
        const index = state.userEnrollments.findIndex(
          (e) => e.id === action.payload.id,
        );
        if (index !== -1) {
          state.userEnrollments[index] = action.payload;
        }
        // Update selectedEnrollment if it matches
        if (state.selectedEnrollment?.id === action.payload.id) {
          state.selectedEnrollment = action.payload;
        }
      })
      .addCase(reactivateEnrollmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Admin Enroll Member
    builder
      .addCase(adminEnrollMemberInCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminEnrollMemberInCourse.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(adminEnrollMemberInCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  setSelectedEnrollment,
  clearEnrollmentError,
  clearUserEnrollments,
} = enrollmentSlice.actions;

// Selectors
export const selectUserEnrollments = (state: { enrollment: EnrollmentState }) =>
  state.enrollment.userEnrollments;

export const selectCourseEnrollments = (state: {
  enrollment: EnrollmentState;
}) => state.enrollment.courseEnrollments;

export const selectSelectedEnrollment = (state: {
  enrollment: EnrollmentState;
}) => state.enrollment.selectedEnrollment;

export const selectEnrollmentLoading = (state: {
  enrollment: EnrollmentState;
}) => state.enrollment.loading;

export const selectEnrollmentError = (state: { enrollment: EnrollmentState }) =>
  state.enrollment.error;

export const selectEnrollmentPagination = (state: {
  enrollment: EnrollmentState;
}) => ({
  total: state.enrollment.total,
  page: state.enrollment.page,
  limit: state.enrollment.limit,
});

export const selectEnrollmentById =
  (enrollmentId: number) => (state: { enrollment: EnrollmentState }) =>
    state.enrollment.userEnrollments.find((e) => e.id === enrollmentId) ||
    state.enrollment.courseEnrollments.find((e) => e.id === enrollmentId);

// Reducer
export default enrollmentSlice.reducer;
